import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { callServer } from '../services/server';
import { onIdTokenChanged, signInWithEmailAndPassword, signOut, type MultiFactorResolver, type User } from 'firebase/auth';
import { resolveWithCode, resolverFor } from '../services/twoStepService';
import type { Lang, Role } from '../types';
import { auth, firebaseEnabled } from '../lib/firebase';
import { setDataScope } from '../services/scope';

const ROLES: Role[] = ['super_admin', 'facility_admin', 'doctor', 'staff'];

export type SignInError = 'invalid' | 'tooMany' | 'network' | 'noAccess' | 'unknown' | 'needsCode' | 'badCode';

interface AppCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  role: Role;
  /** Demo mode only. In real mode the role comes from the login and cannot be changed. */
  setRole: (r: Role) => void;
  facilityId: string;
  /** Demo mode, or super admin browsing clinics. Clinic users are fixed to their own clinic. */
  setFacilityId: (id: string) => void;
  loggedIn: boolean;
  /** False until Firebase has told us whether someone is signed in. Always true in demo mode. */
  authReady: boolean;
  /** Firebase user id in real mode; null in demo mode. */
  uid: string | null;
  /** Real sign-in (Firebase). Resolves on success; rejects with a SignInError code. */
  signIn: (email: string, password: string) => Promise<void>;
  /** 2-step login: finish a sign-in with the 6-digit code from the authenticator app. */
  signInWithCode: (code: string) => Promise<void>;
  /** Demo sign-in (no checks). */
  login: () => void;
  logout: () => void;
}

const Ctx = createContext<AppCtx | null>(null);

function usePersisted<T>(key: string, init: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const saved = localStorage.getItem(key);
      return saved ? (JSON.parse(saved) as T) : init;
    } catch {
      return init;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* storage unavailable */
    }
  }, [key, value]);
  return [value, setValue] as const;
}

function errorCode(err: unknown): SignInError {
  const code = (err as { code?: string })?.code ?? '';
  if (code === 'noAccess') return 'noAccess';
  if (['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found', 'auth/invalid-email', 'auth/user-disabled'].includes(code)) return 'invalid';
  if (code === 'auth/too-many-requests') return 'tooMany';
  if (code === 'auth/network-request-failed') return 'network';
  return 'unknown';
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = usePersisted<Lang>('sc.lang', 'en');
  // Demo state (also used as the super admin's "currently viewed clinic").
  const [demoRole, setDemoRole] = usePersisted<Role>('sc.role', 'facility_admin');
  const [demoFacility, setDemoFacility] = usePersisted<string>('sc.facility', 'sharma-meerut');
  const [demoLoggedIn, setDemoLoggedIn] = usePersisted<boolean>('sc.loggedIn', false);

  // Real (Firebase) state.
  const [authReady, setAuthReady] = useState(!firebaseEnabled);
  const [uid, setUid] = useState<string | null>(null);
  const [claimRole, setClaimRole] = useState<Role | null>(null);
  const [claimFacility, setClaimFacility] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const a = auth;
    if (!firebaseEnabled || !a) return;
    return onIdTokenChanged(a, async (user) => {
      if (!user) {
        setUid(null);
        setClaimRole(null);
        setClaimFacility(null);
        setAuthReady(true);
        return;
      }
      const { claims } = await user.getIdTokenResult();
      const r = claims.role as Role | undefined;
      const f = (claims.facilityId as string | undefined) ?? null;
      // A login without a valid role (or a clinic user without a clinic) gets no access.
      if (!r || !ROLES.includes(r) || (r !== 'super_admin' && !f)) {
        await signOut(a);
        return;
      }
      setUid(user.uid);
      setClaimRole(r);
      setClaimFacility(f);
      setAuthReady(true);
    });
  }, []);

  // After the password (and, with 2-step login, the code): only people with a role and clinic may come in.
  const admit = async (user: User) => {
    if (!auth) return;
    const { claims } = await user.getIdTokenResult(true);
    const r = claims.role as Role | undefined;
    if (!r || !ROLES.includes(r) || (r !== 'super_admin' && !claims.facilityId)) {
      await signOut(auth);
      throw Object.assign(new Error('no access'), { code: 'noAccess' });
    }
    // First successful login: the server sends the "You're all set" email once (never twice). Never blocks sign-in.
    callServer('firstLoginDone', {}).catch(() => undefined);
  };
  const pendingCode = useRef<MultiFactorResolver | null>(null);

  const signInWithCode = async (code: string) => {
    if (!pendingCode.current) throw 'unknown' as SignInError;
    try {
      const cred = await resolveWithCode(pendingCode.current, code);
      pendingCode.current = null;
      await admit(cred.user);
    } catch (err) {
      const c = (err as { code?: string; message?: string });
      throw (c.code === 'auth/invalid-verification-code' || c.message === 'BAD_CODE' ? 'badCode' : errorCode(err)) as SignInError;
    }
  };

  const signIn = async (email: string, password: string) => {
    if (!auth) throw 'unknown' as SignInError;
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      await admit(cred.user);
    } catch (err) {
      const resolver = resolverFor(err);
      if (resolver) {
        pendingCode.current = resolver;
        throw 'needsCode' as SignInError;
      }
      throw errorCode(err);
    }
  };

  const real = firebaseEnabled;
  const role: Role = real ? (claimRole ?? 'staff') : demoRole;
  // Clinic users are locked to their own clinic; super admin may browse (counts only).
  const facilityId = real && claimFacility ? claimFacility : demoFacility;

  // Set during render (not in an effect) so data requests made by child screens see it immediately.
  setDataScope({ facilityId, role, uid });

  const value: AppCtx = {
    lang,
    setLang,
    role,
    setRole: (r) => {
      if (!real) setDemoRole(r);
    },
    facilityId,
    setFacilityId: (id) => {
      if (!real || role === 'super_admin') setDemoFacility(id);
    },
    loggedIn: real ? !!uid : demoLoggedIn,
    authReady,
    uid,
    signIn,
    signInWithCode,
    login: () => {
      if (!real) setDemoLoggedIn(true);
    },
    logout: () => {
      if (real && auth) void signOut(auth);
      else setDemoLoggedIn(false);
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useApp must be used inside AppProvider');
  return c;
}
